import React, { useEffect, useState } from "react";
import {
  DEDUCTION_TYPE_OPTIONS,
  RECORD_KIND_OPTIONS,
  SURFACE_TYPE_OPTIONS,
  addMeasurement,
  deleteMeasurement,
  formatSurfaceType,
  updateMeasurement,
} from "../../../../helpers/estimates";
import {
  MEASUREMENT_INPUT_UNITS,
  MEASUREMENT_RESULT_UNITS,
  convertMeasurementEntry,
  emptyMeasurementEntry,
  formatAreaSqft,
  formatMeasurementArea,
  measurementEntryFromRow,
  formatMeasurementDimensionsWithUnits,
  resolveMeasurementDimensions,
} from "../../../../helpers/measurementModel";
import { computeAreaBreakdown } from "../../../../helpers/estimateLinePricing";
import { EditableGroup, RowStatus } from "./EditableGroup";
import { RowDelete } from "./RowDelete";

/** "Wall 1", "Floor 1", "Deduction 1" — same UI convenience as the phone sheet. */
function defaultName(form, sections) {
  if (form.recordKind === "deduction") {
    return `Deduction ${sections.filter((s) => s.record_kind === "deduction").length + 1}`;
  }
  const label = formatSurfaceType(form.surfaceType) || "Area";
  const count = sections.filter(
    (s) => s.record_kind !== "deduction" && s.surface_type === form.surfaceType,
  ).length;
  return `${label} ${count + 1}`;
}

function formFromRow(section) {
  return {
    name: section.name || "",
    recordKind: section.record_kind || "area",
    surfaceType: section.surface_type || "floor",
    surfaceLabel: section.surface_label || "",
    deductionType: section.deduction_type || "toilet",
    appliesToSurfaceType: section.applies_to_surface_type || "floor",
    appliesToSectionId: section.applies_to_section_id || "",
    notes: section.notes || "",
    ...measurementEntryFromRow(section),
  };
}

function blankForm(recordKind = "area") {
  return {
    name: "",
    recordKind,
    surfaceType: "floor",
    surfaceLabel: "",
    deductionType: "door",
    appliesToSurfaceType: "floor",
    appliesToSectionId: "",
    ...emptyMeasurementEntry(),
  };
}

function areaChoices(sections, selfId) {
  return (sections || []).filter(
    (section) => section.record_kind !== "deduction" && section.section_id && section.section_id !== selfId,
  );
}

function areaPreview(form) {
  try {
    const d = resolveMeasurementDimensions(form);
    return formatMeasurementArea({
      length_in: d.lengthIn,
      width_in: d.widthIn,
      dimension_unit: d.dimensionUnit,
      area_unit: d.areaUnit,
    });
  } catch {
    return "—";
  }
}

function Select({ value, onChange, options, label, col, disabled }) {
  return (
    <select
      className="eg-input"
      aria-label={label}
      data-col={col}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function Num({ value, onChange, label, col, disabled, step = "any", placeholder, autoFocus }) {
  return (
    <input
      className="eg-input eg-input--num"
      type="number"
      inputMode="decimal"
      min="0"
      step={step}
      aria-label={label}
      data-col={col}
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function isMeasurementComplete(form) {
  try {
    resolveMeasurementDimensions(form);
    return true;
  } catch {
    return false;
  }
}

function dimensionsText(form) {
  try {
    const d = resolveMeasurementDimensions(form);
    return formatMeasurementDimensionsWithUnits({ length_in: d.lengthIn, width_in: d.widthIn, dimension_unit: d.dimensionUnit });
  } catch {
    return "";
  }
}

function MeasurementRow({ section, sections, areaInfo, isNew, presetKind, disabled, onSave, onDelete, onDraft, onStatus, focusFirst }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const baseline = isNew ? blankForm(presetKind || "area") : formFromRow(section);
  const idPart = isNew ? (presetKind === "deduction" ? "new-deduction" : "new") : section.section_id;

  return (
    <EditableGroup
      value={baseline}
      isNew={isNew}
      disabled={disabled}
      onSave={onSave}
      onDraft={onDraft}
      onStatus={onStatus}
      isComplete={isNew ? isMeasurementComplete : undefined}
      testId={`measurement-row-${idPart}`}
    >
      {({ draft, update, status }) => {
        const mm = draft.entryUnit === "mm";
        const needsExtras = moreOpen || draft.surfaceType === "other";
        const effectiveName = draft.name || (isNew ? defaultName(draft, sections) : "");
        const areas = areaChoices(sections, isNew ? "" : section.section_id);
        const linked = areas.find((area) => area.section_id === draft.appliesToSectionId);
        const fromLabel = linked
          ? (linked.name || formatSurfaceType(linked.surface_type))
          : `all ${formatSurfaceType(draft.appliesToSurfaceType)}`;
        return (
          <>
            <tr className="eg-row">
              <td>
                <Select
                  label="Surface"
                  col="surface"
                  value={draft.surfaceType}
                  disabled={disabled}
                  options={SURFACE_TYPE_OPTIONS}
                  onChange={(v) => update({ surfaceType: v })}
                />
              </td>
              <td>
                {mm ? (
                  <Num label="Length (mm)" col="length" value={draft.lengthMm} disabled={disabled}
                    onChange={(v) => update({ lengthMm: v })} placeholder="mm" />
                ) : (
                  <span className="eg-pair">
                    <Num label="Length (ft)" col="length" value={draft.lengthFeet} disabled={disabled}
                      onChange={(v) => update({ lengthFeet: v })} placeholder="ft" autoFocus={focusFirst} />
                    <Num label="Length (in)" value={draft.lengthInches} disabled={disabled}
                      onChange={(v) => update({ lengthInches: v })} placeholder="in" />
                  </span>
                )}
              </td>
              <td className="eg-times" aria-hidden="true">×</td>
              <td>
                {mm ? (
                  <Num label="Width (mm)" col="width" value={draft.widthMm} disabled={disabled}
                    onChange={(v) => update({ widthMm: v })} placeholder="mm" />
                ) : (
                  <span className="eg-pair">
                    <Num label="Width (ft)" col="width" value={draft.widthFeet} disabled={disabled}
                      onChange={(v) => update({ widthFeet: v })} placeholder="ft" />
                    <Num label="Width (in)" value={draft.widthInches} disabled={disabled}
                      onChange={(v) => update({ widthInches: v })} placeholder="in" />
                  </span>
                )}
              </td>
              <td>
                <input
                  className="eg-input"
                  aria-label="Label"
                  data-col="label"
                  value={draft.name}
                  disabled={disabled}
                  placeholder={isNew ? effectiveName : "Label"}
                  onChange={(event) => update({ name: event.target.value })}
                />
              </td>
              <td className="eg-num eg-computed" aria-label="Area">
                {draft.recordKind === "deduction" ? "− " : ""}
                {areaPreview(draft)}
                {dimensionsText(draft) ? (
                  <span className="eg-dims" data-testid="measurement-dims">{dimensionsText(draft)}</span>
                ) : null}
                {draft.recordKind === "deduction" ? (
                  <span className="eg-dims">from {fromLabel}</span>
                ) : null}
                {areaInfo ? (
                  <span className="eg-area-math" data-testid="area-math">
                    <span>Gross {formatAreaSqft({ area_sqft: areaInfo.gross })}</span>
                    {areaInfo.deductions.map((item) => (
                      <span key={item.section_id}>− {item.name || "Deduction"} {formatAreaSqft(item)}</span>
                    ))}
                    {areaInfo.legacyShare > 0.05 ? (
                      <span>− other {formatSurfaceType(areaInfo.section.surface_type)} {areaInfo.legacyShare.toFixed(1)} ft²</span>
                    ) : null}
                    <span>Net {areaInfo.net.toFixed(1)} ft²</span>
                  </span>
                ) : null}
              </td>
              <td className="eg-actions">
                <RowStatus status={status} />
                <button
                  type="button"
                  className="eg-link"
                  aria-expanded={needsExtras}
                  onClick={() => setMoreOpen((v) => !v)}
                >
                  More {needsExtras ? "▴" : "▾"}
                </button>
              </td>
              <td className="eg-delete">
                {!isNew ? (
                  <RowDelete label={draft.name || "measurement"} disabled={disabled} onConfirm={() => onDelete(section)} />
                ) : null}
              </td>
            </tr>
            {needsExtras ? (
              <tr className="eg-expander">
                <td colSpan={8}>
                  <div className="eg-expander__grid">
                    <label>
                      <span>Kind</span>
                      <Select label="Kind" value={draft.recordKind} disabled={disabled}
                        options={RECORD_KIND_OPTIONS}
                        onChange={(v) => {
                          if (v !== "deduction") {
                            update({ recordKind: v, appliesToSectionId: "" });
                            return;
                          }
                          const match = areas.find((area) => area.surface_type === draft.surfaceType) || areas[0];
                          update({
                            recordKind: v,
                            appliesToSectionId: match?.section_id || "",
                            appliesToSurfaceType: match?.surface_type || draft.surfaceType || draft.appliesToSurfaceType,
                          });
                        }} />
                    </label>
                    {draft.surfaceType === "other" ? (
                      <label>
                        <span>Surface label</span>
                        <input className="eg-input" aria-label="Surface label" value={draft.surfaceLabel}
                          disabled={disabled} onChange={(e) => update({ surfaceLabel: e.target.value })} />
                      </label>
                    ) : null}
                    {draft.recordKind === "deduction" ? (
                      <>
                        <label>
                          <span>Deduction type</span>
                          <Select label="Deduction type" value={draft.deductionType} disabled={disabled}
                            options={DEDUCTION_TYPE_OPTIONS} onChange={(v) => update({ deductionType: v })} />
                        </label>
                        <label>
                          <span>Applies to</span>
                          {areas.length ? (
                            <Select
                              label="Applies to"
                              value={draft.appliesToSectionId || ""}
                              disabled={disabled}
                              options={[
                                ...(!draft.appliesToSectionId ? [{ value: "", label: `All ${formatSurfaceType(draft.appliesToSurfaceType)} (existing)` }] : []),
                                ...areas.map((area) => ({
                                  value: area.section_id,
                                  label: area.name || formatSurfaceType(area.surface_type),
                                })),
                              ]}
                              onChange={(v) => {
                                const area = areas.find((item) => item.section_id === v);
                                update({
                                  appliesToSectionId: v,
                                  appliesToSurfaceType: area?.surface_type || draft.appliesToSurfaceType,
                                });
                              }}
                            />
                          ) : (
                            <Select label="Applies to surface" value={draft.appliesToSurfaceType} disabled={disabled}
                              options={SURFACE_TYPE_OPTIONS} onChange={(v) => update({ appliesToSurfaceType: v, appliesToSectionId: "" })} />
                          )}
                        </label>
                      </>
                    ) : null}
                    <label>
                      <span>Input unit</span>
                      <Select label="Input unit" value={mm ? "mm" : "ft-in"} disabled={disabled}
                        options={MEASUREMENT_INPUT_UNITS}
                        onChange={(v) => update(convertMeasurementEntry(draft, v))} />
                    </label>
                    <label>
                      <span>Result unit</span>
                      <Select label="Result unit" value={draft.areaUnit === "mm2" ? "mm2" : "ft2"} disabled={disabled}
                        options={MEASUREMENT_RESULT_UNITS} onChange={(v) => update({ areaUnit: v })} />
                    </label>
                  </div>
                </td>
              </tr>
            ) : null}
          </>
        );
      }}
    </EditableGroup>
  );
}

/**
 * Measurements grid. Writes go through the existing addMeasurement /
 * updateMeasurement / deleteMeasurement helpers (area_sqft stays a DB
 * generated column; the Area cell is a preview only).
 */
export function MeasurementsTable({ workType, liveWorkType, actor, editable, onChanged, onDraft, onStatus }) {
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!adding) return undefined;
    document.querySelector('[data-testid="measurement-row-new"] input')?.focus();
    return undefined;
  }, [adding]);
  const sections = workType.sections || [];
  const breakdown = computeAreaBreakdown(liveWorkType || workType);
  const byId = Object.fromEntries(breakdown.map((item) => [item.section.section_id, item]));
  const featureId = workType.estimate_feature_id;

  async function save(section, form) {
    const payload = { ...form, name: form.name || (section ? "" : defaultName(form, sections)) };
    if (section) await updateMeasurement(actor, section.section_id, payload);
    else await addMeasurement(actor, featureId, payload);
    await onChanged();
    if (!section) setAdding(false);
  }

  async function remove(row) {
    if (row.record_kind !== "deduction") {
      const linked = sections.filter(
        (item) => item.record_kind === "deduction" && item.applies_to_section_id === row.section_id,
      );
      if (linked.length) {
        const names = linked.map((item) => item.name || "deduction").join(", ");
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          `"${row.name || "This area"}" has deductions (${names}). Delete this area? Those deductions will keep applying by surface type until you edit them.`,
        );
        if (!ok) return;
      }
    }
    onStatus("saving");
    try {
      await deleteMeasurement(actor, row.section_id);
      await onChanged();
      onStatus("saved");
    } catch (err) {
      onStatus("error", err.message || "Unable to delete measurement.");
    }
  }

  return (
    <section className="ed-section" aria-labelledby="ed-measurements-h">
      <h3 id="ed-measurements-h" className="ed-section__title">Measurements</h3>
      <div className="ed-tablewrap">
        <table className="eg-table">
          <thead>
            <tr>
              <th style={{ width: 104 }}>Surface</th>
              <th style={{ width: 112 }}>Length</th>
              <th style={{ width: 22 }} aria-hidden="true" />
              <th style={{ width: 112 }}>Width</th>
              <th>Label</th>
              <th className="eg-num" style={{ width: 140 }}>Area</th>
              <th style={{ width: 88 }} aria-label="Actions" />
              <th style={{ width: 44 }} aria-label="Remove" />
            </tr>
          </thead>
          {sections.map((section) => (
            <MeasurementRow
              key={section.section_id}
              section={section}
              sections={sections}
              areaInfo={section.record_kind === "deduction" ? null : byId[section.section_id]}
              disabled={!editable}
              onSave={(form) => save(section, form)}
              onDraft={(draft) => onDraft?.(section.section_id, draft)}
              onDelete={remove}
              onStatus={onStatus}
            />
          ))}
          {editable && adding ? (
            <MeasurementRow
              isNew
              sections={sections}
              focusFirst
              onSave={(form) => save(null, form)}
              onStatus={onStatus}
            />
          ) : null}
        </table>
      </div>
      {editable && !adding ? (
        <button type="button" className="ed-add" aria-label="Add measurement" onClick={() => setAdding(true)}>
          + Add measurement
        </button>
      ) : null}
      {editable ? <p className="ed-hint">Saved lines are the estimate. Add measurement opens one line. It saves when you leave it (or press Enter).</p> : null}
    </section>
  );
}
